import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Plus, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { anonSupabase } from "@/integrations/supabase/anonClient";

export type OfficeSelection = {
  id?: string | null;
  name: string;
};

interface OfficeLocationComboboxProps {
  companyId?: string | null;
  value: OfficeSelection | null | undefined;
  onChange: (value: OfficeSelection | null) => void;
  disabled?: boolean;
  /** Use the public (anon) client — required during registration before sign-in. */
  useAnon?: boolean;
  placeholder?: string;
}

type OfficeRow = { id: string; location_name: string };

/**
 * Typeahead for selecting an office location, with an explicit
 * "create new office" path so we don't accumulate near-duplicates.
 *
 * If no `companyId` is provided (e.g. very first user creating a brand-new
 * company), falls back to a plain text input.
 */
export function OfficeLocationCombobox({
  companyId,
  value,
  onChange,
  disabled,
  useAnon,
  placeholder = "Select or create an office",
}: OfficeLocationComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [offices, setOffices] = useState<OfficeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const client = useAnon ? anonSupabase : supabase;

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && !containerRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    if (!companyId) {
      setOffices([]);
      return;
    }
    setLoading(true);
    (async () => {
      const { data, error } = await client.rpc("lookup_office_locations", {
        p_company_id: companyId,
      });
      if (cancelled) return;
      if (error) {
        console.error("[OfficeLocationCombobox] lookup failed:", error);
        setOffices([]);
      } else {
        setOffices((data as OfficeRow[]) || []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, client]);

  const trimmedQuery = query.trim();
  const lowerQuery = trimmedQuery.toLowerCase();

  const filtered = useMemo(() => {
    if (!lowerQuery) return offices;
    return offices.filter((o) =>
      o.location_name.toLowerCase().includes(lowerQuery)
    );
  }, [offices, lowerQuery]);

  const exactMatch = useMemo(
    () =>
      offices.find(
        (o) => o.location_name.toLowerCase() === lowerQuery
      ) || null,
    [offices, lowerQuery]
  );

  // No company context yet — fall back to a plain input (first user of a new company).
  if (!companyId) {
    return (
      <Input
        placeholder="Office location name"
        value={value?.name ?? ""}
        onChange={(e) =>
          onChange(e.target.value ? { name: e.target.value } : null)
        }
        disabled={disabled}
      />
    );
  }

  const triggerLabel = value?.name
    ? value.name
    : <span className="text-muted-foreground">{placeholder}</span>;

  const isNewSelection = value && !value.id;

  const handleSelectExisting = (office: OfficeRow) => {
    onChange({ id: office.id, name: office.location_name });
    setQuery("");
    setOpen(false);
  };

  const handleCreateNew = () => {
    if (!trimmedQuery) return;
    onChange({ name: trimmedQuery });
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls="office-location-options"
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          className="w-full justify-between font-normal"
        >
          <span className="flex items-center gap-2 truncate">
            {triggerLabel}
            {isNewSelection && (
              <Badge variant="secondary" className="text-[10px] uppercase">
                New
              </Badge>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      {open && (
        <div
          id="office-location-options"
          className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          <div className="border-b p-2">
            <Input
              placeholder="Search offices..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {loading && (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading offices...
              </div>
            )}

            {!loading && filtered.length === 0 && !trimmedQuery && (
              <div className="px-3 py-4 text-sm text-muted-foreground">
                No offices yet — start typing to add one.
              </div>
            )}

            {!loading && filtered.length > 0 && (
              <div className="py-1">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  Existing offices
                </div>
                {filtered.map((office) => (
                  <button
                    key={office.id}
                    type="button"
                    onClick={() => handleSelectExisting(office)}
                    className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value?.id === office.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate">{office.location_name}</span>
                  </button>
                ))}
              </div>
            )}

            {trimmedQuery && !exactMatch && (
              <div className="border-t py-1">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  Create new
                </div>
                <button
                  type="button"
                  onClick={handleCreateNew}
                  className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <span className="truncate">
                    Create new office: <span className="font-medium">"{trimmedQuery}"</span>
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OfficeLocationCombobox;
